import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  PanelLeft,
  Plus, 
  Folder, 
   
  Star, 
  Clock, 
  Settings, 
  LogOut,
  Palette,
  BookOpen,
  
  MoreVertical,
  Share2,
  Pencil,
  Pin,
  Trash2,
  Globe,
  Loader2,
  ArrowUpCircle,
  Check,
  Users
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "next-themes";
import { formatDistanceToNow } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShareDialog } from "./ShareDialog";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { usePrefetchAnalysis } from "@/hooks/useAnalysisData";
import { useTranslation } from "react-i18next";
import { languages } from "@/i18n/config";

// Lazy load heavy modal


interface RecentAnalysis {
  id: string;
  title: string;
  updated_at: string;
  is_pinned: boolean;
  is_shared: boolean | null;
  share_token: string | null;
}

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { tier, isAdmin } = useSubscription();
  const { t, i18n } = useTranslation();
  
  const [user, setUser] = useState<{ email: string; id: string } | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [recentAnalyses, setRecentAnalyses] = useState<RecentAnalysis[]>([]);
  
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareAnalysis, setShareAnalysis] = useState<RecentAnalysis | null>(null);
  const [unreadInviteCount, setUnreadInviteCount] = useState(0);
  const queryClient = useQueryClient();
  const prefetchAnalysis = usePrefetchAnalysis();

  const handleUpgrade = () => {
    navigate('/upgrade');
  };

  const getPlanLabel = () => {
    if (isAdmin) return 'Admin';
    if (tier === 'pro') return 'Pro plan';
    if (tier === 'core') return 'Core plan';
    return 'Free plan';
  };

  // Fetch user and profile
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser({ email: user.email || "", id: user.id });
        
        // Fetch profile
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name")
          .eq("user_id", user.id)
          .single();
        
        if (profile?.display_name) {
          setDisplayName(profile.display_name);
        }
      }
    };

    fetchUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser({ email: session.user.email || "", id: session.user.id });
      } else {
        setUser(null);
        setDisplayName(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch recent analyses with React Query for cache invalidation
  const { data: recentAnalysesData } = useQuery({
    queryKey: ['recentAnalyses', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data } = await supabase
        .from("analyses")
        .select("id, title, updated_at, is_pinned, is_shared, share_token")
        .eq("user_id", user.id)
        .order("is_pinned", { ascending: false })
        .order("updated_at", { ascending: false })
        .limit(10);

      return data || [];
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Update local state when query data changes
  useEffect(() => {
    if (recentAnalysesData) {
      setRecentAnalyses(recentAnalysesData);
    } else if (!user) {
      setRecentAnalyses([]);
    }
  }, [recentAnalysesData, user]);

  // Fetch unread invite count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) {
        setUnreadInviteCount(0);
        return;
      }
      
      if (!user?.email) return;

      const { count } = await supabase
        .from("analysis_invites")
        .select("*", { count: "exact", head: true })
        .eq("email", user.email.toLowerCase())
        .neq("invited_by", user.id)
        .is("accepted_at", null);

      setUnreadInviteCount(count || 0);
    };

    fetchUnreadCount();
  }, [user]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const getUserInitial = () => {
    if (displayName) return displayName.charAt(0).toUpperCase();
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return "U";
  };

  const getDisplayNameOrEmail = () => {
    if (displayName) return displayName;
    if (user?.email) return user.email.split("@")[0];
    return "User";
  };

  // Strip author from title for compact display (e.g., "The Great Gatsby by Fitzgerald" -> "The Great Gatsby")
  const getBookTitleOnly = (title: string) => {
    const byIndex = title.toLowerCase().lastIndexOf(" by ");
    if (byIndex > 0) {
      return title.substring(0, byIndex);
    }
    return title;
  };

  // Recents action handlers
  const handleShare = (analysis: RecentAnalysis) => {
    setShareAnalysis(analysis);
    setShareDialogOpen(true);
  };

  const handleShareChange = (isShared: boolean, shareToken: string | null) => {
    // Update local state and invalidate query to refresh data
    if (shareAnalysis) {
      setShareAnalysis({ ...shareAnalysis, is_shared: isShared, share_token: shareToken });
    }
    queryClient.invalidateQueries({ queryKey: ['recentAnalyses'] });
  };

  const handleRename = async (analysisId: string, newTitle: string) => {
    if (!newTitle.trim()) {
      setRenamingId(null);
      return;
    }
    
    const { error } = await supabase
      .from("analyses")
      .update({ title: newTitle.trim() })
      .eq("id", analysisId);
    
    if (error) {
      toast.error(t('toast.renameFailed'));
    } else {
      toast.success(t('toast.renamed'));
      queryClient.invalidateQueries({ queryKey: ['recentAnalyses'] });
    }
    setRenamingId(null);
  };

  const handlePin = async (analysisId: string, currentlyPinned: boolean) => {
    const { error } = await supabase
      .from("analyses")
      .update({ is_pinned: !currentlyPinned })
      .eq("id", analysisId);
    
    if (error) {
      toast.error(t('toast.pinFailed'));
    } else {
      toast.success(currentlyPinned ? t('toast.unpinned') : t('toast.pinned'));
      queryClient.invalidateQueries({ queryKey: ['recentAnalyses'] });
    }
  };

  const handleDelete = async (analysisId: string) => {
    const { error } = await supabase
      .from("analyses")
      .delete()
      .eq("id", analysisId);
    
    if (error) {
      toast.error(t('toast.deleteFailed'));
    } else {
      toast.success(t('toast.deleted'));
      queryClient.invalidateQueries({ queryKey: ['recentAnalyses'] });
    }
  };

  // Get current analysis ID from path
  const currentAnalysisId = location.pathname.startsWith("/analysis/") 
    ? location.pathname.split("/")[2] 
    : null;

  // Keep all recents visible including the current analysis

  const navItems = [
    { 
      label: t('nav.newAnalysis'), 
      icon: Plus, 
      action: () => navigate("/"),
      isHighlighted: true 
    },
    { 
      label: t('nav.allAnalyses'), 
      icon: Folder, 
      path: "/analyses" 
    },
    { 
      label: t('nav.favorites'), 
      icon: Star, 
      path: "/favorites" 
    },
    { 
      label: "Shared with Me", 
      icon: Users, 
      path: "/shared-with-me",
      badgeCount: unreadInviteCount
    },
    ...(isAdmin ? [{
      label: "AI Costs",
      icon: Settings,
      path: "/admin/costs"
    }] : []),
  ];

  return (
    <>
      <Sidebar collapsible="icon" className="border-r border-border">
        <SidebarHeader className="p-3">
          <div className="flex items-center justify-center">
            {isCollapsed ? (
              // Collapsed: Smart logo trigger with floating tooltip
              <div className="relative">
                {/* Fixed-size icon button - maintains alignment with other rail icons */}
                <button
                  onMouseEnter={() => setIsLogoHovered(true)}
                  onMouseLeave={() => setIsLogoHovered(false)}
                  onClick={toggleSidebar}
                  className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors cursor-pointer"
                >
                  {/* Crossfade between BookOpen and PanelLeft icons */}
                  <BookOpen 
                    className={cn(
                      "h-5 w-5 text-primary absolute transition-opacity duration-150",
                      isLogoHovered ? "opacity-0" : "opacity-100"
                    )} 
                  />
                  <PanelLeft 
                    className={cn(
                      "h-5 w-5 text-primary absolute transition-opacity duration-150",
                      isLogoHovered ? "opacity-100" : "opacity-0"
                    )} 
                  />
                </button>

                {/* Floating tooltip - positioned OUTSIDE the rail */}
                {isLogoHovered && (
                  <div className="absolute left-full ml-3 top-0 translate-y-1.5
                                  bg-popover border border-border rounded-md px-3 py-1.5 shadow-md
                                  text-sm text-foreground whitespace-nowrap z-50
                                  animate-fade-in">
                    {t('nav.openSidebar')}
                  </div>
                )}
              </div>
            ) : (
              // Expanded: logo, title, and collapse button
              <div className="flex items-center gap-2 w-full">
                <Link 
                  to="/" 
                  className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                    <BookOpen className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-semibold text-white">AnnotateAI</span>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleSidebar}
                  className="h-8 w-8 ml-auto cursor-pointer hover:bg-muted transition-colors"
                >
                  <PanelLeft className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </SidebarHeader>

        <SidebarContent>
          {/* Main Navigation */}
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => (
                  <SidebarMenuItem key={item.label}>
                    <SidebarMenuButton
                      asChild={!!item.path}
                      onClick={item.action}
                      isActive={item.path ? location.pathname === item.path : false}
                      className={cn(
                        item.isHighlighted && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground shadow-sm"
                      )}
                      tooltip={item.label}
                    >
                      {item.path ? (
                        <Link to={item.path} className="flex items-center gap-2 w-full">
                          <item.icon className="h-4 w-4" />
                          <span className="flex-1">{item.label}</span>
                          {item.badgeCount && item.badgeCount > 0 && (
                            <Badge variant="secondary" className="ml-auto text-xs h-5 min-w-5 flex items-center justify-center">
                              {item.badgeCount}
                            </Badge>
                          )}
                        </Link>
                      ) : (
                        <>
                          <item.icon className="h-4 w-4" />
                          <span>{item.label}</span>
                        </>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Recents Section - Hidden when collapsed */}
          {!isCollapsed && user && recentAnalyses.length > 0 && (
            <>
              <Separator className="mx-3 my-2" />
              <SidebarGroup>
                <SidebarGroupLabel>{t('nav.recents')}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {recentAnalyses.slice(0, 8).map((analysis) => (
                      <SidebarMenuItem key={analysis.id} className="group/recent">
                        <SidebarMenuButton
                          asChild
                          isActive={location.pathname === `/analysis/${analysis.id}`}
                          className="h-auto py-2 pr-8"
                        >
                          <Link to={`/analysis/${analysis.id}`} onMouseEnter={() => prefetchAnalysis(analysis.id)}>
                            {analysis.is_pinned ? (
                              <Pin className="h-3.5 w-3.5 shrink-0 text-primary" />
                            ) : (
                              <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            )}
                            <div className="flex flex-col min-w-0 gap-0.5">
                              {renamingId === analysis.id ? (
                                <input
                                  type="text"
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onBlur={() => handleRename(analysis.id, renameValue)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleRename(analysis.id, renameValue);
                                    if (e.key === 'Escape') setRenamingId(null);
                                  }}
                                  onClick={(e) => e.preventDefault()}
                                  autoFocus
                                  className="text-sm bg-transparent border-b border-primary outline-none w-full"
                                />
                              ) : (
                                <span className="truncate text-sm">{getBookTitleOnly(analysis.title)}</span>
                              )}
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                {analysis.is_shared && (
                                  <Globe className="h-3 w-3 text-primary" />
                                )}
                                <span>{formatDistanceToNow(new Date(analysis.updated_at), { addSuffix: true })}</span>
                              </div>
                            </div>
                          </Link>
                        </SidebarMenuButton>
                        
                        {/* More options dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover/recent:opacity-100 hover:bg-muted transition-opacity"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="h-4 w-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" side="right" className="w-40">
                            <DropdownMenuItem 
                              onClick={() => handleShare(analysis)}
                              className="cursor-pointer"
                            >
                              <Share2 className="mr-2 h-4 w-4" />
                              {t('nav.share')}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                setRenamingId(analysis.id);
                                setRenameValue(analysis.title);
                              }}
                              className="cursor-pointer"
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('nav.rename')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handlePin(analysis.id, analysis.is_pinned)}
                              className="cursor-pointer"
                            >
                              <Pin className="mr-2 h-4 w-4" />
                              {analysis.is_pinned ? t('nav.unpin') : t('nav.pin')}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                setPendingDeleteId(analysis.id);
                                setDeleteDialogOpen(true);
                              }}
                              className="cursor-pointer text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('nav.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </>
          )}
        </SidebarContent>

        {/* User Account Footer */}
        {user && (
          <SidebarFooter className="p-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-start gap-2 h-auto py-2",
                    isCollapsed && "justify-center px-2"
                  )}
                >
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {getUserInitial()}
                    </AvatarFallback>
                  </Avatar>
                  {!isCollapsed && (
                    <div className="flex flex-col items-start min-w-0">
                      <span className="text-sm font-medium truncate">
                        {getDisplayNameOrEmail()}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {getPlanLabel()}
                      </span>
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top" className="w-56">
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <Palette className="mr-2 h-4 w-4" />
                    {t('nav.theme')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
                      <DropdownMenuRadioItem value="light" className="cursor-pointer">
                        {t('nav.light')}
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="dark" className="cursor-pointer">
                        {t('nav.dark')}
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <Globe className="mr-2 h-4 w-4" />
                    {t('nav.language')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
                    {languages.map((lang) => (
                      <DropdownMenuItem
                        key={lang.code}
                        onClick={() => {
                          i18n.changeLanguage(lang.code);
                          document.documentElement.dir = lang.code === 'ar' ? 'rtl' : 'ltr';
                        }}
                        className="cursor-pointer"
                      >
                        <span className="mr-2">{lang.flag}</span>
                        {lang.name}
                        {i18n.language === lang.code && (
                          <Check className="ml-auto h-4 w-4" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                {!isAdmin && tier !== 'pro' && (
                  <DropdownMenuItem onClick={handleUpgrade} className="cursor-pointer">
                    <ArrowUpCircle className="mr-2 h-4 w-4" />
                    {t('nav.upgradePlan')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer">
                  <Settings className="mr-2 h-4 w-4" />
                  {t('nav.settings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('nav.signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        )}
      </Sidebar>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dialogs.deleteAnalysis')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('dialogs.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDeleteId) {
                  handleDelete(pendingDeleteId);
                  setPendingDeleteId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share dialog controlled from sidebar */}
      {shareAnalysis && (
        <ShareDialog
          analysisId={shareAnalysis.id}
          isShared={shareAnalysis.is_shared ?? false}
          shareToken={shareAnalysis.share_token}
          onShareChange={handleShareChange}
          analysisData={{ title: shareAnalysis.title }}
          open={shareDialogOpen}
          onOpenChange={(open) => {
            setShareDialogOpen(open);
            if (!open) setShareAnalysis(null);
          }}
          hideTrigger
        />
      )}
    </>
  );
}
